import { authenticatedFetch } from '@/lib/auth'
import { API_BASE_URL, API_ENDPOINTS } from '@/lib/config'
import { createCoalescedFetcher } from '@/lib/requestCoalesce'
import { trackCheckoutStarted } from '@/lib/analytics/conversion'
import {
  buildCheckoutUrl,
  navigateToProductCheckout,
} from '@/lib/checkoutNavigation'
import { shouldBlockInterviewEntry } from '@/lib/interviewInProgressBlock'
import { getMyResume, type MyResumeResponse } from '@/lib/resumeApi'

export const MOCK_INTERVIEW_PATH = '/mock-interview'

export const MOCK_INTERVIEW_OPTIONS_PATH = `${MOCK_INTERVIEW_PATH}/options`

/** Second entry: pick role + up to five skills + experience instead of uploading a resume. */
export const MOCK_INTERVIEW_ROLE_BASED_PATH = `${MOCK_INTERVIEW_PATH}/role-based-interview`

/** Whether `pathname` is the mock interview route or a sub-route (e.g. options). */
export function isMockInterviewPath(pathname: string): boolean {
  const path = pathname.split('?')[0]?.split('#')[0] ?? ''
  return path === MOCK_INTERVIEW_PATH || path.startsWith(`${MOCK_INTERVIEW_PATH}/`)
}

export type SkillAssessmentCheckoutProduct = {
  productId: string
  productPrice: number
}

type SkillAssessmentAccessResponse = {
  product_id?: string
  product_price?: number
  detail?: string
}

export type SkillAssessmentAccessResult =
  | { kind: 'granted' }
  | { kind: 'payment_required'; product: SkillAssessmentCheckoutProduct }
  | { kind: 'unauthorized' }
  | { kind: 'error'; message: string }

/** True when the user should complete upload/extraction on `/mock-interview` before checkout. */
export function shouldUseResumeInterviewUploadFlow(
  resume: MyResumeResponse | null,
): boolean {
  return resume === null || resume.extraction_status !== 'completed'
}

const _skillAssessmentAccessFetcher = createCoalescedFetcher<SkillAssessmentAccessResult>(0)

/** Calls `GET /user/access/skill_assessment` (mock interview payment gate).
 * Coalesces concurrent duplicate calls; no caching so post-payment access changes stay fresh. */
export function checkSkillAssessmentAccess(): Promise<SkillAssessmentAccessResult> {
  return _skillAssessmentAccessFetcher('skill_assessment', _checkSkillAssessmentAccessImpl)
}

async function _checkSkillAssessmentAccessImpl(): Promise<SkillAssessmentAccessResult> {
  try {
    const accessResponse = await authenticatedFetch(
      `${API_BASE_URL}${API_ENDPOINTS.USER_ACCESS('skill_assessment')}`,
    )

    if (accessResponse.status === 403) {
      const errorData = (await accessResponse
        .json()
        .catch(() => ({}))) as SkillAssessmentAccessResponse
      if (
        errorData.product_id &&
        errorData.product_price !== undefined
      ) {
        return {
          kind: 'payment_required',
          product: {
            productId: errorData.product_id,
            productPrice: errorData.product_price,
          },
        }
      }
      return {
        kind: 'error',
        message:
          'Payment required but product details are missing. Please contact support.',
      }
    }

    if (accessResponse.status === 401) {
      return { kind: 'unauthorized' }
    }

    if (!accessResponse.ok) {
      const errorData = (await accessResponse
        .json()
        .catch(() => ({}))) as SkillAssessmentAccessResponse
      return {
        kind: 'error',
        message:
          typeof errorData.detail === 'string'
            ? errorData.detail
            : 'Could not verify access. Please try again.',
      }
    }

    return { kind: 'granted' }
  } catch {
    return {
      kind: 'error',
      message: 'Could not verify access. Please try again.',
    }
  }
}

export type MockInterviewEntryResolution =
  | { action: 'resume_interview' }
  | { action: 'blocked_in_progress' }
  | { action: 'checkout'; product: SkillAssessmentCheckoutProduct }
  | { action: 'unauthorized' }
  | { action: 'error'; message: string }

/**
 * Decides where to send the user when starting a mock interview from the dashboard
 * (or any caller that should skip `/mock-interview` when checkout is required).
 */
export async function resolveMockInterviewEntry(): Promise<MockInterviewEntryResolution> {
  if (await shouldBlockInterviewEntry()) {
    return { action: 'blocked_in_progress' }
  }

  const resume = await getMyResume()
  if (shouldUseResumeInterviewUploadFlow(resume)) {
    return { action: 'resume_interview' }
  }

  const access = await checkSkillAssessmentAccess()
  if (access.kind === 'payment_required') {
    return { action: 'checkout', product: access.product }
  }
  if (access.kind === 'unauthorized') {
    return { action: 'unauthorized' }
  }
  if (access.kind === 'error') {
    return { action: 'error', message: access.message }
  }
  return { action: 'resume_interview' }
}

type MockInterviewRouter = {
  push: (href: string) => void
  replace: (href: string) => void
}

export function redirectToSkillAssessmentCheckout(
  router: MockInterviewRouter,
  product: SkillAssessmentCheckoutProduct,
  options?: { replace?: boolean; returnTo?: string },
): void {
  const params = {
    productId: product.productId,
    paymentPlanType: 'skill_assessment' as const,
    productPrice: product.productPrice,
    returnTo: options?.returnTo ?? MOCK_INTERVIEW_PATH,
  }
  if (options?.replace) {
    try {
      trackCheckoutStarted({
        plan: 'skill_assessment',
        source: 'free_15_minute_interview',
      })
    } catch {
      // ignore
    }
    router.replace(buildCheckoutUrl(params))
    return
  }
  navigateToProductCheckout(router, params)
}

export function applyMockInterviewEntryResolution(
  router: MockInterviewRouter,
  resolution: MockInterviewEntryResolution,
  options?: { replaceCheckout?: boolean },
): void {
  switch (resolution.action) {
    case 'blocked_in_progress':
      break
    case 'checkout':
      redirectToSkillAssessmentCheckout(router, resolution.product, {
        replace: options?.replaceCheckout ?? true,
      })
      break
    case 'resume_interview':
      router.push(MOCK_INTERVIEW_PATH)
      break
    case 'unauthorized':
      router.replace('/')
      break
    case 'error':
      break
  }
}

export function mockInterviewEntryErrorMessage(
  resolution: MockInterviewEntryResolution,
): string | null {
  return resolution.action === 'error' ? resolution.message : null
}
